import { NavLink, Outlet, Link } from "react-router-dom";
import { LayoutDashboard, BarChart3, LogOut } from "lucide-react";
import { BrandLogo } from "@/shared/ui";
import { ThemeToggle } from "@/features/theme/ThemeToggle";
import { useLogout, useIsAdmin, useAuth } from "@/features/auth";
import { cn } from "@/shared/lib/cn";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Заявки", icon: LayoutDashboard, end: true },
] as const;

const ADMIN_ITEMS = [
  { to: "/admin/stats", label: "Статистика", icon: BarChart3 },
] as const;

export function DashboardLayout() {
  const { mutate: logout, isPending } = useLogout();
  const isAdmin = useIsAdmin();
  const { user } = useAuth();

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors",
      isActive
        ? "bg-ocean-100 text-ocean-700 dark:bg-ocean-900/30 dark:text-ocean-300"
        : "text-sand-600 hover:bg-sand-100 dark:text-sand-400 dark:hover:bg-sand-800",
    );

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Sidebar (desktop) / Top bar (mobile) */}
      <aside className="md:w-60 md:min-h-screen border-b md:border-b-0 md:border-r border-sand-200 dark:border-sand-700 bg-white dark:bg-sand-900 p-4 flex md:flex-col gap-2">
        <div className="flex items-center justify-between md:mb-6 w-full">
          <Link to="/">
            <BrandLogo />
          </Link>
          <ThemeToggle className="md:hidden" />
        </div>

        <nav className="flex md:flex-col gap-1 flex-1 overflow-x-auto md:overflow-visible">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={navLinkClass}>
              <item.icon className="h-4 w-4" />
              <span className="hidden md:inline">{item.label}</span>
            </NavLink>
          ))}
          {isAdmin && ADMIN_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} className={navLinkClass}>
              <item.icon className="h-4 w-4" />
              <span className="hidden md:inline">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block mt-auto space-y-3 pt-4 border-t border-sand-200 dark:border-sand-700">
          <ThemeToggle />
          {user && (
            <p className="text-xs text-sand-500 dark:text-sand-400 truncate">
              {user.firstName} {user.lastName}
            </p>
          )}
          <button
            onClick={() => logout()}
            disabled={isPending}
            className="flex items-center gap-2 text-sm text-sand-500 hover:text-coral-500 transition-colors cursor-pointer"
          >
            <LogOut className="h-4 w-4" />
            Выйти
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 p-4 md:p-6">
        <Outlet />
      </main>
    </div>
  );
}
